import React, { useEffect, useState } from 'react';
import AddressManagementService, { AddressHistoryRecord, ParsedAddress } from '../../services/addressManagementService';
import JsonViewer from '../common/JsonViewer';

interface AddressHistoryTimelineProps {
    traceId: string;
    refreshTrigger?: number;
}

const AddressDisplay: React.FC<{ address: ParsedAddress }> = ({ address }) => {
    // Cast to any to access PascalCase properties from the JSON data
    const addr = address as any;
    return (
        <div className="address-display">
            <div className="fw-medium">{addr.Street1}</div>
            {addr.Street2 && <div className="text-muted small">{addr.Street2}</div>}
            <div className="text-muted small">
                {addr.City}, {addr.StateCode} {addr.Zip}{addr.ZipSuffix ? `-${addr.ZipSuffix}` : ''}
            </div>
            {addr.Latitude && addr.Longitude && (
                <div className="text-muted small font-monospace">
                    ({addr.Latitude}, {addr.Longitude})
                </div>
            )}
            <div className="mt-2">
                {addr.IsGoogleValid !== undefined && (
                    <span className={`badge me-1 ${addr.IsGoogleValid ? 'bg-success' : 'bg-danger'}`}>
                        Google: {addr.GoogleScore ?? 'N/A'}
                    </span>
                )}
                {addr.IsRadarValid !== undefined && (
                    <span className={`badge ${addr.IsRadarValid ? 'bg-success' : 'bg-danger'}`}>
                        Radar: {addr.RadarScore ?? 'N/A'}
                    </span>
                )}
            </div>
        </div>
    );
};

const AddressHistoryTimeline: React.FC<AddressHistoryTimelineProps> = ({ traceId, refreshTrigger }) => {
    const [history, setHistory] = useState<AddressHistoryRecord[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

    const toggleExpanded = (recordId: number) => {
        const newExpanded = new Set(expandedItems);
        if (newExpanded.has(recordId)) {
            newExpanded.delete(recordId);
        } else {
            newExpanded.add(recordId);
        }
        setExpandedItems(newExpanded);
    };

    useEffect(() => {
        const fetchHistory = async () => {
            setIsLoading(true);
            setError(null);
            try {
                console.log(`Fetching address history for traceId: ${traceId}`);
                const data = await AddressManagementService.getAddressHistory(traceId);
                setHistory(data);
                console.log(`Successfully fetched ${data.length} history records`);
            } catch (err: any) {
                const errorMessage = err?.response?.status === 404
                    ? 'No history records found for this address.'
                    : err?.response?.data?.message || err.message || 'Failed to load history';
                setError(errorMessage);
                console.error(`Failed to fetch history for traceId ${traceId}:`, err);
                setHistory([]); // Clear any existing data
            } finally {
                setIsLoading(false);
            }
        };

        if (traceId) {
            fetchHistory();
        }
    }, [traceId, refreshTrigger]);

    if (isLoading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted">Loading history...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-warning">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error}
            </div>
        );
    }

    if (history.length === 0) {
        return (
            <div className="text-center py-5 text-muted">
                <i className="bi bi-clock-history fs-1 opacity-50"></i>
                <p className="mt-2">No history records found for this address.</p>
            </div>
        );
    }

    return (
        <div className="position-relative py-2">
            {/* Summary Header */}
            <div className="mb-3">
                <div className="card border-primary">
                    <div className="card-body py-2">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div>
                                <h6 className="mb-0 text-primary">
                                    <i className="bi bi-clock-history me-2"></i>
                                    Address Change History
                                </h6>
                                <small className="text-muted">
                                    {history.length} change{history.length !== 1 ? 's' : ''} recorded
                                </small>
                            </div>
                            <div className="d-flex gap-1 flex-wrap">
                                {Array.from(new Set(history.map(h => h.changeSource))).map(source => (
                                    <span key={source} className="badge bg-secondary">{source}</span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Vertical Line */}
            <div
                className="position-absolute h-100 border-start border-2"
                style={{ left: '13px', top: 0, zIndex: 0, opacity: 0.2 }}
            ></div>

            {history.map((record) => {
                const isExpanded = expandedItems.has(record.id);
                let previousAddress: ParsedAddress = {};
                let newAddress: ParsedAddress = {};

                try {
                    previousAddress = JSON.parse(record.previousAddressSnapshot);
                    newAddress = JSON.parse(record.newAddressSnapshot);
                } catch (err) {
                    console.error('Failed to parse address snapshots:', err);
                }

                const prev = previousAddress as any;
                const next = newAddress as any;

                const getChangeIcon = (source: string) => {
                    switch (source.toLowerCase()) {
                        case 'coordinatecorrection': return 'bi-geo-alt';
                        case 'manualupdate': return 'bi-person-check';
                        case 'api': return 'bi-code';
                        default: return 'bi-pencil';
                    }
                };

                const confidenceBadgeClass = (value?: string) => {
                    switch ((value || '').toUpperCase()) {
                        case 'HIGH': return 'bg-success';
                        case 'MEDIUM': return 'bg-warning text-dark';
                        case 'LOW': return 'bg-danger';
                        default: return 'bg-secondary';
                    }
                };

                // Compact list of fields that actually changed between the two snapshots
                const fieldChanges: Array<{ label: string; prev: any; next: any; changed?: boolean; isConfidence?: boolean }> = [
                    { label: 'Street', prev: prev.Street1, next: next.Street1 },
                    { label: 'City', prev: prev.City, next: next.City },
                    { label: 'State', prev: prev.StateCode, next: next.StateCode },
                    { label: 'ZIP', prev: prev.Zip, next: next.Zip },
                    {
                        label: 'Coordinates',
                        prev: `${prev.Latitude ?? 'N/A'}, ${prev.Longitude ?? 'N/A'}`,
                        next: `${next.Latitude ?? 'N/A'}, ${next.Longitude ?? 'N/A'}`,
                        changed: prev.Latitude !== next.Latitude || prev.Longitude !== next.Longitude,
                    },
                    { label: 'Geofence', prev: prev.GeoFenceConfidence, next: next.GeoFenceConfidence, isConfidence: true },
                ].filter(f => (f.changed !== undefined ? f.changed : f.prev !== f.next));

                return (
                    <div key={record.id} className="mb-2 position-relative ps-4">
                        {/* Status Dot */}
                        <div
                            className="position-absolute rounded-circle d-flex align-items-center justify-content-center bg-primary text-white"
                            style={{ left: '4px', top: '6px', width: '20px', height: '20px', zIndex: 1, fontSize: '0.7rem' }}
                        >
                            <i className={getChangeIcon(record.changeSource)}></i>
                        </div>

                        <div className="card shadow-sm border-0">
                            <div className="card-body p-2">
                                {/* Compact header */}
                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-1 mb-1">
                                    <div className="d-flex align-items-center flex-wrap gap-1">
                                        <span className="fw-semibold small">
                                            {new Date(record.createdAt).toLocaleString()}
                                        </span>
                                        <span className="badge bg-secondary">{record.changeSource}</span>
                                        {record.changedBy && (
                                            <span className="badge bg-info text-dark">{record.changedBy}</span>
                                        )}
                                    </div>
                                    <small className="text-muted font-monospace">#{record.id}</small>
                                </div>

                                {/* Reason */}
                                {record.changeReason && (
                                    <div className="text-muted small mb-1">
                                        <i className="bi bi-info-circle me-1"></i>
                                        {record.changeReason}
                                    </div>
                                )}

                                {/* Compact key changes */}
                                {fieldChanges.length > 0 ? (
                                    <div className="small d-flex flex-column gap-1">
                                        {fieldChanges.map((f) => (
                                            <div key={f.label} className="d-flex align-items-center flex-wrap gap-1">
                                                <span className="text-muted" style={{ minWidth: '78px' }}>{f.label}:</span>
                                                {f.isConfidence ? (
                                                    <>
                                                        <span className={`badge ${confidenceBadgeClass(f.prev)}`}>{f.prev || 'none'}</span>
                                                        <i className="bi bi-arrow-right text-muted"></i>
                                                        <span className={`badge ${confidenceBadgeClass(f.next)}`}>{f.next || 'none'}</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <span className="text-danger text-decoration-line-through">{f.prev || 'N/A'}</span>
                                                        <i className="bi bi-arrow-right text-muted"></i>
                                                        <span className="text-success fw-medium">{f.next || 'N/A'}</span>
                                                    </>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    record.changeSummary && (
                                        <div className="small text-muted">{record.changeSummary}</div>
                                    )
                                )}

                                {/* Expand toggle */}
                                <button
                                    className="btn btn-link btn-sm p-0 mt-1 text-decoration-none d-inline-flex align-items-center gap-1"
                                    onClick={() => toggleExpanded(record.id)}
                                >
                                    <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'}`}></i>
                                    {isExpanded ? 'Hide' : 'Show'} full snapshots
                                </button>

                                {isExpanded && (
                                    <div className="row g-2 mt-1">
                                        <div className="col-md-6">
                                            <JsonViewer
                                                data={previousAddress}
                                                title="Previous State (JSON)"
                                                maxHeight="300px"
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <JsonViewer
                                                data={newAddress}
                                                title="New State (JSON)"
                                                maxHeight="300px"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default AddressHistoryTimeline;
